import moment from 'moment';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { List, Skeleton } from 'antd';

import InfiniteScroll from 'react-infinite-scroll-component';
import ChatItem from './chat-item';

import { ChatsCollection, FetchChatParams, SmsChat, UnreadCountData } from '../../models';
import * as actions from '../../store/actions';

import { isEmpty } from 'lodash';
import { ChatFilterTypeEnum } from '../../../../shared/constants/enums';
import { AppDispatch, RootState } from '../../../../store';

import './styles.less';

let CHAT_PAGE_COUNT = 10;
const CHAT_ITEM_SIZE = 60;
const ADDITIONAL_ITEMS_COUNT = 2;

export type ChatItemListProps = {
  newUnreadCount: UnreadCountData | undefined;
  updateMessageControl: () => void;
  chatSearchValue: string;
  pageNumber: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  fetchChatData: FetchChatParams | undefined;
  chatFilterValue: ChatFilterTypeEnum;
};

const ChatItemList = ({
  newUnreadCount,
  updateMessageControl,
  chatSearchValue,
  pageNumber,
  setPageNumber,
  fetchChatData,
  chatFilterValue,
}: ChatItemListProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const didMount = useRef(false);

  const hasUpdateResult = useSelector((state: RootState) => state.smses.hasUpdateResult);
  const messageControlParameters = useSelector((state: RootState) => state.smses.messageControlParameters);
  const messageControlParametersLoading = useSelector((state: RootState) => state.smses.messageControlParametersLoading);
  const chatItems = useSelector((state: RootState) => state.smsChat.localChats);

  useEffect(() => {
    if (!didMount.current) {
      CHAT_PAGE_COUNT = Math.round(document.documentElement.clientHeight / CHAT_ITEM_SIZE) + ADDITIONAL_ITEMS_COUNT;
      dispatch(
        actions.setLocalChats({
          hasMore: true,
          chatList: [],
          lastChangeDateTime: null,
        } as ChatsCollection),
      );

      didMount.current = true;
    }
  }, []);

  useEffect(() => {
    if (messageControlParameters) {
      fetchChat({
        pageNumber,
        chatSearchValue,
        callback: undefined,
        filter: chatFilterValue,
      });
    }
  }, [messageControlParameters]);

  useEffect(() => {
    if (chatItems && chatItems.chatList) {
      const index = chatItems.chatList.findIndex((val) => val.phoneNumber === newUnreadCount?.phoneNumber);

      if (index > -1) {
        const newChatItems = { ...chatItems } as ChatsCollection;
        const newChatList = newChatItems.chatList.slice();
        const chatItem = { ...newChatList[index] } as SmsChat;

        chatItem.unreadCount = newUnreadCount?.newUnreadCount ?? 0;

        newChatList[index] = chatItem;
        newChatItems.chatList = newChatList;

        dispatch(actions.setLocalChats(newChatItems));
        dispatch(actions.setSelectedChatItem(chatItem));
      }

      if (!chatItems.chatList.some((x) => x.unreadCount > 0)) {
        const element = document.querySelector('#showNewSmsLabel');

        if (element) {
          (element as HTMLElement).style.display = 'none';
        }
      }
    }
  }, [newUnreadCount]);

  useEffect(() => {
    if (hasUpdateResult && hasUpdateResult.chat) {
      setPageNumber(1);
      fetchChat({
        pageNumber: 1,
        chatSearchValue,
        callback: undefined,
        filter: chatFilterValue,
      });
    }
  }, [hasUpdateResult]);

  useEffect(() => {
    if (fetchChatData) {
      fetchChat(fetchChatData);
    }
  }, [fetchChatData]);

  const loadMoreData = () => {
    setPageNumber(pageNumber + 1);
    fetchChat({
      pageNumber: pageNumber + 1,
      chatSearchValue,
      callback: undefined,
      filter: chatFilterValue,
    });
  };

  const fetchChat = ({ pageNumber, chatSearchValue, callback, filter }: FetchChatParams) => {
    if (!messageControlParameters) {
      return;
    }

    return new Promise((resolve, reject) => {
      if (isEmpty(messageControlParameters.myNumber)) {
        resolve({});
        return;
      }

      dispatch<Promise<ChatsCollection>>(
        actions.getChats({
          pageNumber: pageNumber,
          pageSize: CHAT_PAGE_COUNT,
          chatSearchValue: chatSearchValue,
          smsServiceType: messageControlParameters.smsServiceType,
          ownerAccountUserId: messageControlParameters.ownerAccountUserId,
          myPhoneNumber: messageControlParameters.myNumber,
          filter: filter,
        }),
      ).then((results) => {
        let newChatItems = { ...chatItems } as ChatsCollection;

        if (newChatItems && newChatItems.chatList && newChatItems.chatList.length > 0 && pageNumber > 1) {
          const newChatList = newChatItems.chatList.slice();
          newChatList.push(...results.chatList);
          newChatItems.chatList = newChatList;
          newChatItems.hasMore = results.hasMore;
        } else {
          newChatItems = results;
        }

        dispatch(actions.setLocalChats(newChatItems));

        if (typeof callback === 'function') {
          callback(newChatItems);
        }

        resolve(newChatItems);
      });
    });
  };

  return (
    <div className="chat-scrollable-div custom-scrollbar" id="chatScrollableDiv">
      <InfiniteScroll
        dataLength={chatItems && chatItems.chatList && chatItems.chatList.length > -1 ? chatItems.chatList.length : 0}
        next={loadMoreData}
        hasMore={(chatItems && chatItems.hasMore) || messageControlParametersLoading}
        loader={<Skeleton paragraph={{ rows: 1 }} active />}
        scrollableTarget="chatScrollableDiv"
      >
        {chatItems && chatItems.chatList && chatItems.chatList.length > 0 ? (
          <List
            dataSource={chatItems.chatList}
            split={false}
            renderItem={(item) => (
              <List.Item key={item.smsChatId} className="infinite-scroll-item" title={moment(item.lastSmsDate).format('MMM Do YYYY, h:mm:ss A')}>
                <List.Item.Meta description={<ChatItem updateMessageControl={updateMessageControl} item={item} />} />
              </List.Item>
            )}
          />
        ) : undefined}
      </InfiniteScroll>
    </div>
  );
};

export default ChatItemList;
